"use client";

import { useEffect } from "react";

/**
 * HeartbeatProvider — fires POST /api/profile/heartbeat on a 60s
 * interval while the tab is visible, so the user counts as "online"
 * for the presence dot even when they're passively scrolling the
 * board. See migration 084 + /api/profile/heartbeat for the
 * server-side story.
 *
 * Behavior:
 *   - Mounts at the AppShell level so it's always running while the
 *     app is open.
 *   - Only fires when document.visibilityState === 'visible'. A
 *     backgrounded tab counts as offline (matches user expectation
 *     that closing the app = going offline).
 *   - 60s cadence is deliberately slow — server has a 1-min debounce,
 *     and the dot's threshold is 5 min, so we only need to keep
 *     bumping inside that window. Slower interval = less client
 *     bandwidth, less server load, longer battery life.
 *   - Fires once immediately on mount + on visibilitychange → visible
 *     so the user goes online without waiting for the next tick.
 *
 * No payload, no response handling beyond the implicit fire-and-
 * forget. Failures are silent — the user just stays at whatever
 * last_active_at they had, which is fine.
 */
export function HeartbeatProvider() {
  useEffect(() => {
    const beat = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState !== "visible") return;
      // Fire-and-forget. keepalive lets the request complete even if
      // the user backgrounds the tab mid-flight.
      fetch("/api/profile/heartbeat", {
        method: "POST",
        keepalive: true,
      }).catch(() => {});
    };

    // Initial beat on mount — user shouldn't have to wait 60s after
    // opening the app to count as online.
    beat();
    const intervalId = setInterval(beat, 60_000);

    // Re-fire when the tab returns to visible (user switched back
    // from another tab / unlocked phone). Without this, a returning
    // user would wait up to 60s before being marked online again.
    const onVisibility = () => {
      if (document.visibilityState === "visible") beat();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return null;
}
